import { ReactNode, forwardRef, useMemo } from "react";
import { Form as FormikForm, useField, useFormikContext } from "formik";
import classnames from "classnames";
import ReactSelect, { components, createFilter } from "react-select";
import { makeTemplateComponent } from "../Elements/Elements.tsx";
import {
  IconClose,
  IconUpload,
  IconError,
  IconSearch,
  Toggle,
} from "../Icon/Icon.tsx";
import Button, { TYPE_BUTTON } from "../Button/Button.tsx";
import styles from "./Form.module.css";

export const Form = (props: any) => (
  <FormikForm className={styles.form} {...props} />
);
export const Label = makeTemplateComponent("label", styles.label);
export const Input = forwardRef(({ className, ...rest }: Input, ref) => (
  <input {...rest} className={classnames(styles.input, className)} ref={ref} />
));
Input.displayName = "Input";
type Input = {
  className?: string;
};

export const InputSearch = forwardRef(
  (
    { className, error, disabled, icon, submitable, ...rest }: InputSearch,
    ref,
  ) => (
    <div
      ref={ref}
      className={classnames(
        styles.inputSearch,
        { [styles.disabled]: disabled },
        className,
      )}
    >
      {error && (
        <div className={styles.icon}>
          <IconError />
        </div>
      )}
      <div className={styles.inputDiv}>
        {!submitable && <div className={styles.icon}>{icon}</div>}
        <input {...rest} disabled={disabled} className={styles.input} />
        {submitable && (
          <Button type={TYPE_BUTTON.submit} className={styles.searchButton}>
            <IconSearch />
          </Button>
        )}
      </div>
    </div>
  ),
);
InputSearch.displayName = "InputSearch";
type InputSearch = {
  className?: string;
  error?: string | null;
  disabled?: boolean;
  icon?: ReactNode;
  submitable?: boolean;
};

export const TextValue = ({ label, value }: TextValue) => {
  return (
    <div className={styles.field}>
      {label && <Label>{label}</Label>}
      <div className={styles.valueField}>
        <div className={styles.value}>{value}</div>
      </div>
    </div>
  );
};

type TextValue = {
  label?: string;
  value?: string;
};

const Field = ({
  label,
  name,
  type,
  placeholder = "",
  disabled,
  className,
  icon,
  submitable,
  ...rest
}: Field) => {
  const [field, meta] = useField(name);
  const error = meta.touched ? meta.error : null;

  // Permet d'effacer le filtre recherche si on appuie sur le bouton "réinitialiser"
  field.value = field.value ?? "";

  return (
    <DivWithError
      error={error}
      className={classnames(
        styles.field,
        {
          [styles.noLabel]: !label,
        },
        className,
      )}
    >
      {label && <Label htmlFor={name}>{label}</Label>}
      <InputSearch
        id={name}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        icon={icon}
        submitable={submitable}
        {...field}
        {...rest}
        error={error}
      />
    </DivWithError>
  );
};
type Field = {
  name: string;
  type: string;
  label?: string | ReactNode;
  placeholder?: string;
  disabled?: boolean;
  computeError?: () => string;
  className?: string;
  icon?: ReactNode;
  submitable?: boolean;
};

export const Password = ({
  label,
  name,
  placeholder = "",
  disabled,
  className,
}: Password) => (
  <Field
    label={label}
    type="password"
    name={name}
    placeholder={placeholder}
    disabled={disabled}
    className={className}
  />
);

type Password = {
  name: string;
  label?: string;
  placeholder: string;
  disabled?: boolean;
  computeError?: () => string;
  className?: string;
};

export const Text = ({
  label,
  name,
  placeholder = "",
  disabled,
  className,
  obligatoire = false,
  submitable = false,
  ...rest
}: Text) => (
  <Field
    label={
      <>
        {label}{" "}
        {obligatoire && <span className={"text-danger fw-bold"}>*</span>}
      </>
    }
    type="text"
    name={name}
    placeholder={placeholder}
    disabled={disabled}
    icon={!submitable && <IconSearch />}
    submitable={submitable}
    className={className}
    {...rest}
  />
);

type Text = {
  name: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  computeError?: () => string;
  className?: string;
  obligatoire?: boolean;
  submitable?: boolean;
};

export const TextField = ({
  name,
  placeholder = "",
  disabled,
  ...rest
}: TextField) => {
  const [field] = useField(name);

  return (
    <input
      id={name}
      name={name}
      type="text"
      className={styles.textInput}
      placeholder={placeholder}
      disabled={disabled}
      {...field}
      {...rest}
    />
  );
};
type TextField = {
  name: string;
  type: string;
  placeholder?: string;
  disabled?: boolean;
  computeError?: () => string;
  className?: string;
};

export const LargeTextField = ({
  name,
  placeholder = "",
  disabled,
  ...rest
}: LargeTextField) => {
  const [field] = useField(name);

  return (
    <textarea
      id={name}
      name={name}
      type="text"
      className={styles.largeTextInput}
      placeholder={placeholder}
      disabled={disabled}
      {...field}
      {...rest}
    />
  );
};
type LargeTextField = {
  name: string;
  type: string;
  placeholder?: string;
  disabled?: boolean;
  computeError?: () => string;
  className?: string;
};

export const NumberField = ({
  name,
  min,
  max,
  placeholder = "",
  disabled = false,
  onChange: onChangeCallback,
  className,
  ...rest
}: NumberFieldModel) => {
  const [field] = useField(name);

  return (
    <input
      id={name}
      name={name}
      type="number"
      min={min}
      max={max}
      className={classnames(styles.numberInput, className)}
      placeholder={placeholder}
      disabled={disabled}
      {...field}
      onChange={(e) => {
        field.onChange(e);
        if (onChangeCallback) {
          onChangeCallback(e.target.value);
        }
      }}
      {...rest}
    />
  );
};
type NumberFieldModel = {
  name: string;
  min?: number;
  max?: number;
  placeholder?: string;
  disabled?: boolean;
  onChange?: any;
  className?: string;
};

export const ErrorMessage = ({ error }: ErrorMessage) =>
  error ? <div className={styles.errorMessage}>{error}</div> : null;

type ErrorMessage = {
  error?: string | null;
};

export const DivWithError = ({
  error,
  className,
  innerRef,
  children,
  ...rest
}: DivWithError) => (
  <div
    {...rest}
    ref={innerRef}
    className={classnames(className, error ? styles.error : null)}
  >
    {children}
    <ErrorMessage error={error} />
  </div>
);

type DivWithError = {
  name: string;
  className?: string;
  error?: string | null;
  innerRef?: (...args: any[]) => any;
  children: ReactNode;
};

export const FileInput = ({ label, name, accept, className }: FileInput) => {
  const [field, meta] = useField(name);
  const { setFieldValue } = useFormikContext();
  const error = meta.touched ? meta.error : null;
  return (
    <>
      <DivWithError name={name} error={error}>
        {label && (
          <label htmlFor={name} className={styles.label}>
            {label}&nbsp;:
          </label>
        )}
        <label className={styles.fileInputButton}>
          <div className={className}>
            {field?.value?.name ?? " "} &nbsp;
            <input
              id={name}
              type="file"
              accept={accept}
              onChange={(e) => {
                setFieldValue(name, e.target.files[0]);
              }}
              hidden
            />
          </div>
          <span className={styles.fileInputIcon}>
            <IconUpload />
          </span>
        </label>
        <ErrorMessage name={name} />
      </DivWithError>
    </>
  );
};

type FileInput = {
  label: string;
  name: string;
  accept: string;
  onChange: (...args: any[]) => any;
  className?: string;
};

export const VerticalLine = makeTemplateComponent("div", styles.verticalLine);

export const Title = makeTemplateComponent("div", styles.title);

export const Checkbox = ({
  disabled,
  name,
  value = false,
  className,
  ...rest
}: Checkbox) => {
  return (
    <span
      className={classnames(
        styles.checkbox,
        {
          [styles.disabled]: disabled,
        },
        className,
      )}
    >
      <input
        id={name}
        type="checkbox"
        {...rest}
        value={value.toString()}
        checked={value}
        disabled={disabled}
        className={styles.input}
      />
      <label htmlFor={name}>
        <Toggle toggled={value} />
      </label>
    </span>
  );
};
type Checkbox = {
  name: string;
  disabled?: boolean | undefined;
  value: boolean;
  className?: string | undefined;
};

export const CheckboxInput = ({
  name,
  label,
  disabled,
  className,
  value,
  onChange: onChangeCallback,
  tooltip,
}: CheckboxInput) => {
  const [field, meta] = useField(name);
  const error = meta.touched ? meta.error : null;

  return (
    <DivWithError
      error={error}
      className={classnames(
        styles.field,
        {
          [styles.noLabel]: !label,
        },
        className,
      )}
    >
      {label && (
        <label htmlFor={field.name}>
          <span className={styles.label}>
            {label}&nbsp;{label.trim().endsWith("?") ? "" : ":"}
          </span>
          {tooltip ?? tooltip}
        </label>
      )}
      <Checkbox
        {...field}
        value={field.value ?? value}
        disabled={disabled}
        onChange={(event) => {
          if (onChangeCallback) {
            onChangeCallback(event.target.checked);
          }
          field.onChange(event);
        }}
      />
    </DivWithError>
  );
};
type CheckboxInput = {
  name: string;
  label?: string | undefined;
  disabled?: boolean | undefined;
  value: boolean;
  className?: string | undefined;
  onChange?: (value: boolean) => void;
  tooltip?: ReactNode | undefined;
};

export const RealCheckbox = ({
  disabled,
  name,
  value = false,
  className,
  ...rest
}: RealCheckbox) => {
  return (
    <span
      className={classnames(
        styles.realCheckbox,
        {
          [styles.disabled]: disabled,
        },
        className,
      )}
    >
      <label htmlFor={name}>
        <input
          id={name}
          type="checkbox"
          {...rest}
          value={value.toString()}
          checked={value}
          disabled={disabled}
          className={styles.input}
        />
      </label>
    </span>
  );
};
type RealCheckbox = {
  name: string;
  disabled?: boolean | undefined;
  value: boolean;
  className?: string | undefined;
};

export const RealCheckboxInput = ({
  name,
  label,
  disabled,
  className,
  value,
  onChange: onChangeCallback,
}: RealCheckboxInput) => {
  const [field, meta] = useField(name);
  const error = meta.touched ? meta.error : null;

  return (
    <DivWithError
      error={error}
      className={classnames(
        styles.field,
        {
          [styles.noLabel]: !label,
        },
        className,
      )}
    >
      <RealCheckbox
        {...field}
        value={value ?? field.value}
        disabled={disabled}
        onChange={(event) => {
          if (onChangeCallback) {
            onChangeCallback(event.target.checked);
          }
          field.onChange(event);
        }}
      />
      {label && (
        <label
          className={classnames(styles.labelCheckbox)}
          htmlFor={field.name}
        >
          {label}&nbsp;
        </label>
      )}
    </DivWithError>
  );
};
type RealCheckboxInput = {
  name: string;
  label?: string | undefined;
  disabled: boolean;
  value: boolean;
  className?: string | undefined;
  onChange: (value: boolean) => void;
};

const Option = ({ data, ...rest }: Option) => {
  const value = data;
  return (
    <components.Option
      {...rest}
      className={classnames({
        [styles.invalide]: value.valide != null && !value.valide,
      })}
    />
  );
};
type Option = {
  data: { valide?: boolean | undefined };
};

// eslint-disable-next-line react/display-name
const ReactSelectInput = (name) => (props) => {
  return <components.Input {...props} name={name} />;
};

const _SelectInput = ({
  name,
  label,
  disabled,
  options,
  isMulti = false,
  isClearable = false,
  obligatoire = false,
  className,
  onChange: onChangeCallback,
  getOptionValue = (option) => option.id,
  toOptionLabelFunc = (option) => option.label,
  value,
  ...props
}: _SelectInput) => {
  const [field, { error }, { setValue, setTouched }] = useField(name);
  const Input = useMemo(() => ReactSelectInput(name), [name]);

  return (
    <div className={classnames(styles.field, className)}>
      {label && (
        <label htmlFor={name} className={styles.label}>
          {label}&nbsp;:{" "}
          {obligatoire && <span className={styles.obligatoire}>*</span>}
        </label>
      )}

      <DivWithError name={field.name}>
        <Select
          menuPlacement="auto"
          components={{ Option, Input }}
          className={styles.reactSelect}
          options={options || []}
          getOptionLabel={toOptionLabelFunc}
          getOptionValue={getOptionValue}
          isMulti={isMulti}
          value={value ?? field.value}
          onChange={(values: object[]) => {
            if (onChangeCallback) {
              onChangeCallback(values);
            }
            setValue(values);
          }}
          onBlur={() => setTouched(true)}
          isDisabled={disabled}
          closeMenuOnSelect={!isMulti}
          isClearable={isClearable}
          isSearchable={true}
          placeholder="Sélectionnez..."
          noOptionsMessage={() => "Pas d'options disponibles pour cette valeur"}
          name={name}
          {...props}
        />
        <ErrorMessage error={error} />
      </DivWithError>
    </div>
  );
};
type _SelectInput = {
  label?: string | ReactNode | undefined;
  name: string;
  disabled: boolean;
  options: object[];
  toOptionLabelFunc?: () => string | undefined;
  isMulti: boolean;
  isClearable: boolean;
  className?: string | undefined;
  onChange: (values: object[]) => void;
  getOptionValue?: () => any | undefined;
  value?: string | undefined;
  obligatoire?: boolean;
};

const _Select = ({
  options,
  isDisabled = false,
  isMulti = false,
  isClearable = false,
  isSearchable = false,
  getOptionLabel = (option) => option.label,
  getOptionValue = (option) => option.id,
  noOptionsMessage = () => "Pas d'options disponibles pour cette valeur",
  filterOption = createFilter({
    ignoreCase: true,
    ignoreAccents: true,
    matchFrom: "any",
    stringify: (option) => `${option.label}`,
    trim: true,
  }),
  onChange,
  value,
  defaultValue,
  className,
  placeholder = "Sélectionnez...",
  menuPlacement = "auto",
  ...props
}: _Select) => {
  return (
    <ReactSelect
      menuPlacement={menuPlacement}
      className={className}
      classNamePrefix="select"
      options={options || []}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      isMulti={isMulti}
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      isDisabled={isDisabled}
      closeMenuOnSelect={!isMulti}
      isClearable={isClearable}
      isSearchable={isSearchable}
      placeholder={placeholder}
      noOptionsMessage={noOptionsMessage}
      filterOption={filterOption}
      {...props}
    />
  );
};
type _Select = {
  options: object[];
  isDisabled?: boolean;
  isMulti: boolean;
  isClearable: boolean;
  isSearchable: boolean;
  getOptionLabel?: () => string | undefined;
  getOptionValue?: () => string | undefined;
  noOptionsMessage?: () => string | undefined;
  filterOption?: () => boolean | undefined;
  onChange?: () => void | undefined;
  value?: object | undefined;
  defaultValue?: object;
  className?: string | undefined;
  placeholder?: string | undefined;
  menuPlacement?: string | undefined;
};

export const Select = (props) => <_Select {...props} />;
export const SelectInput = (props) => <_SelectInput {...props} />;
export const MultiSelectInput = (props) => <_SelectInput {...props} isMulti />;

export const ClearableLine = ({
  children,
  label,
  disabled = false,
}: ClearableLine) => {
  const { setFieldValue } = useFormikContext();

  // Peut être amélioré
  const names = children.flatMap((d) => d?.props?.name).filter(Boolean);

  const clearLine = () => {
    names.forEach((v) => {
      setFieldValue(v, null, true);
    });
  };

  return (
    <div className={styles.clearableLine}>
      {label && <div className={styles.label}>{label}</div>}
      <div className={styles.clearableWrapper}>
        {children}
        <div
          className={styles.clearButton}
          onClick={!disabled && clearLine}
          disabled={disabled}
        >
          <IconClose />
          &nbsp;Effacer
        </div>
      </div>
    </div>
  );
};
type ClearableLine = {
  children: ReactNode;
  label?: ReactNode;
  disabled?: boolean;
};

export const MultiCheckboxInput = ({
  name,
  choices = [],
  disabled,
}: MultiCheckboxInput) => {
  const [, meta, helpers] = useField(name);
  const { value: values } = meta;

  const { setValue } = helpers;
  const CheckboxButton = ({ data }: { data: object }) => (
    <label
      className={classnames(styles.multiCbCheckbox, {
        [styles.multiCbCheckboxActive]:
          values.map((v) => v.id).indexOf(data.id) !== -1,
      })}
      title={data.label}
    >
      <input
        type={"checkbox"}
        id={data.id}
        name={data.id}
        value={data.id}
        disabled={disabled}
        checked={values.map((v) => v.id).indexOf(data.id) !== -1}
        onClick={(e) => {
          setValue(
            e.target.checked
              ? [...values, data]
              : values.filter((v) => v.id !== data.id),
          );
        }}
        className={styles.input}
      />
      {data.label}
    </label>
  );
  return choices?.length > 0 ? (
    <>
      <div className={styles.block}>
        {choices.map((d, k) => (
          <CheckboxButton key={k} data={d} />
        ))}
        <div
          className={classnames(styles.multiCbCheckbox, {
            [styles.multiCbCheckboxActive]: choices.every((value) =>
              values.map((v) => v.id).includes(value.id),
            ),
          })}
          title={"Tous"}
          key={-1}
          onClick={() => {
            setValue(choices);
          }}
        >
          Tous
        </div>
      </div>
      <ErrorMessage error={meta.error} />
    </>
  ) : null;
};
type MultiCheckboxInput = {
  name: string;
  choices: object[];
  disabled?: boolean;
};

export const LegendeChampsObligatoires = () => {
  return (
    <div className={styles.legende}>
      <span className={styles.obligatoire}>*</span>&nbsp; : Champ requis
    </div>
  );
};
